// src/features/tasks/components/UncheckedEquipmentDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Autorenew as RotationIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { groupEquipmentByRoom } from '../utils/equipmentGrouping';

const hasRotation = (link) => link.rotation_options?.length > 0;

/**
 * Shown before completing a task whose equipment has rotating values and
 * isn't all checked off: unchecked equipment keeps its current value on the
 * next occurrence instead of moving on. Lists all the task's equipment, in the
 * same order as the Opgaveudstyr card, and lets the user check off the rest.
 */
export default function UncheckedEquipmentDialog({ warning, busy, onCancel, onContinue }) {
  const { t } = useTranslation(['tasks', 'common']);
  const links = useMemo(() => warning?.links || [], [warning]);
  // Same order as the Opgaveudstyr card: by room, then list order
  const ordered = useMemo(
    () => groupEquipmentByRoom(links, null).flatMap(group => group.items),
    [links],
  );
  const unchecked = links.filter(link => !link.is_checked);

  // Starts as checked on the task; whatever is ticked here when continuing
  // is saved back to the task before completing
  const checkedOnTask = useMemo(
    () => new Set(links.filter(l => l.is_checked).map(l => l.task_equipment_id)),
    [links],
  );
  const [selected, setSelected] = useState(checkedOnTask);
  useEffect(() => { setSelected(checkedOnTask); }, [checkedOnTask]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Name the value in the message ("testplacering"), falling back to a
  // generic word when a link has no label
  const fallback = t('tasks:equipment_warning.value_fallback', 'roterende værdi');
  const label = [...new Set(
    unchecked.filter(hasRotation).map(l => (l.rotation_label || fallback).toLowerCase()),
  )].join('/');

  const renderRow = (link) => {
    const isChecked = selected.has(link.task_equipment_id);
    const canToggle = !busy;
    const options = link.rotation_options || [];
    const nextMark = options.length ? options[(link.rotation_index + 1) % options.length] : null;

    return (
      <Box
        key={link.task_equipment_id}
        onClick={() => canToggle && toggle(link.task_equipment_id)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          borderRadius: 1,
          cursor: canToggle ? 'pointer' : 'default',
          ...(canToggle && { '&:hover': { bgcolor: 'action.hover' } }),
        }}
      >
        <Checkbox
          size="small"
          checked={isChecked}
          disabled={!canToggle}
          // The row handles the click
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(link.task_equipment_id)}
        />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {link.equipment?.name}
          {link.equipment?.room?.name && (
            <Typography component="span" variant="body2" color="text.secondary">
              {' '}({link.equipment.room.name})
            </Typography>
          )}
        </Typography>
        {hasRotation(link) && (
          <Chip
            size="small"
            variant="outlined"
            color={isChecked ? 'primary' : 'default'}
            icon={<RotationIcon />}
            label={`${link.rotation_label || t('tasks:equipment.rotation_label', 'Rotation')}: ${
              isChecked ? `${link.current_mark} → ${nextMark}` : link.current_mark
            }`}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog open={Boolean(warning)} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('tasks:equipment_warning.title', 'Ikke alt udstyr er tjekket af')}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('tasks:equipment_warning.message', {
            label,
            defaultValue: 'Udstyr, der ikke er tjekket af, får ikke sin {{label}} flyttet videre. Næste gang står den samme {{label}} igen.',
          })}
        </Alert>
        <Stack spacing={0.25}>{ordered.map(renderRow)}</Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy}>
          {t('common:buttons.cancel', 'Annuller')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => onContinue([...selected])} disabled={busy}>
          {selected.size > 0
            ? t('tasks:equipment_warning.continue_selected', {
              count: selected.size,
              defaultValue: 'Fortsæt med valgte ({{count}})',
            })
            : t('tasks:equipment_warning.continue_partial', 'Fortsæt uden')}
        </Button>
        <Button
          variant="contained"
          onClick={() => onContinue(links.map(l => l.task_equipment_id))}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {t('tasks:equipment_warning.check_all', 'Tjek alle af og fortsæt')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
